import { useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, Loader2, Gift } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { api, resolveImg, money } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const SLOTS = [
  'Express Courier · 09:00 – 12:00',
  'Morning · 10:00 – 13:00',
  'Afternoon · 13:00 – 17:00',
  'Evening · 18:00 – 21:00',
];
const GIFT_FEE = 15;

export default function Checkout() {
  const { items, subtotal } = useCart();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    shipping_name: '', shipping_address: '', shipping_city: '', shipping_country: '',
    delivery_date: '', delivery_slot: SLOTS[0], delivery_note: '',
    gift_wrap: false, gift_message: '',
  });
  const minDate = new Date(Date.now() + 86400000).toISOString().split('T')[0];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pay = async () => {
    if (items.length === 0) { toast.error('Your bag is empty'); return; }
    if (!form.shipping_name || !form.shipping_address || !form.shipping_city || !form.shipping_country) { toast.error('Complete your shipping details'); return; }
    if (!form.delivery_date) { toast.error('Select a delivery date'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/checkout', {
        items: items.map((i) => (i.product.bundle_id
          ? { bundle_id: i.product.bundle_id, quantity: i.quantity }
          : { product_id: i.product.id, quantity: i.quantity, size: i.size || null })),
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
      setBusy(false);
    }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-16 max-w-6xl mx-auto py-16">
      <h1 className="font-serif text-4xl lg:text-5xl mb-12">Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3 space-y-10">
          <section>
            <p className="eyebrow mb-5">Shipping Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Label className="text-xs">Full Name</Label><Input data-testid="checkout-name" value={form.shipping_name} onChange={(e) => set('shipping_name', e.target.value)} className="mt-1.5 bg-surface border-border" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Address</Label><Input data-testid="checkout-address" value={form.shipping_address} onChange={(e) => set('shipping_address', e.target.value)} className="mt-1.5 bg-surface border-border" /></div>
              <div><Label className="text-xs">City</Label><Input data-testid="checkout-city" value={form.shipping_city} onChange={(e) => set('shipping_city', e.target.value)} className="mt-1.5 bg-surface border-border" /></div>
              <div><Label className="text-xs">Country</Label><Input data-testid="checkout-country" value={form.shipping_country} onChange={(e) => set('shipping_country', e.target.value)} className="mt-1.5 bg-surface border-border" /></div>
            </div>
          </section>

          <section>
            <p className="eyebrow mb-5 flex items-center gap-2"><CalendarClock size={14} /> Schedule Your Delivery</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Delivery Date</Label>
                <Input data-testid="delivery-scheduler-date-input" type="date" min={minDate} value={form.delivery_date} onChange={(e) => set('delivery_date', e.target.value)} className="mt-1.5 bg-surface border-border" />
              </div>
              <div>
                <Label className="text-xs">Courier Window</Label>
                <Select value={form.delivery_slot} onValueChange={(v) => set('delivery_slot', v)}>
                  <SelectTrigger data-testid="delivery-scheduler-time-slot-select" className="mt-1.5 bg-surface border-border"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface-elevated border-border">
                    {SLOTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Delivery Note (optional)</Label>
                <Textarea data-testid="delivery-note" value={form.delivery_note} onChange={(e) => set('delivery_note', e.target.value)} placeholder="Concierge desk, gift note, access instructions…" className="mt-1.5 bg-surface border-border" />
              </div>
            </div>
          </section>

          <section>
            <p className="eyebrow mb-5 flex items-center gap-2"><Gift size={14} /> Gift Wrapping</p>
            <div className="border border-border bg-surface p-5">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">Add Ke-Sa signature gift wrapping</Label>
                  <p className="text-xs text-muted-foreground mt-1">Hand-tied box with a personal note · +{money(GIFT_FEE)}</p>
                </div>
                <Switch data-testid="gift-wrap-switch" checked={form.gift_wrap} onCheckedChange={(v) => set('gift_wrap', v)} />
              </div>
              {form.gift_wrap && (
                <Textarea data-testid="gift-message-input" value={form.gift_message} onChange={(e) => set('gift_message', e.target.value)}
                  placeholder="Your gift message…" className="mt-4 bg-background border-border" />
              )}
            </div>
          </section>
        </div>

        <div className="lg:col-span-2">
          <div className="border border-border bg-surface p-6 sticky top-28">
            <p className="eyebrow mb-5">Order Summary</p>
            <div className="space-y-4 mb-6">
              {items.map((i) => (
                <div key={`${i.product.id}|${i.size || ''}`} className="flex gap-3">
                  <img src={resolveImg(i.product.images?.[0])} alt="" className="w-14 h-16 object-cover bg-surface-elevated" />
                  <div className="flex-1">
                    <p className="text-sm font-serif leading-tight">{i.product.name}</p>
                    <p className="text-xs text-muted-foreground">Qty {i.quantity}{i.size ? ` · Size ${i.size}` : ''}</p>
                  </div>
                  <p className="font-mono text-sm text-gold">{money(i.product.price * i.quantity)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-4 space-y-2 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-mono">{money(subtotal)}</span>
              </div>
              {form.gift_wrap && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Gift wrapping</span>
                  <span className="font-mono">{money(GIFT_FEE)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="text-sm text-muted-foreground">Total</span>
                <span data-testid="checkout-total" className="font-mono text-lg text-gold">{money(subtotal + (form.gift_wrap ? GIFT_FEE : 0))}</span>
              </div>
            </div>
            <button data-testid="stripe-payment-form-submit" onClick={pay} disabled={busy}
              className="w-full bg-gold text-primary-foreground py-4 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <><Loader2 size={15} className="animate-spin" /> Redirecting…</> : 'Pay Securely'}
            </button>
            <p className="text-[11px] text-muted-foreground text-center mt-3">Secure payment via Stripe · Test card 4242 4242 4242 4242</p>
          </div>
        </div>
      </div>
    </div>
  );
}
